import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { SoSvgIconUI } from "../so-svg-icon/so-svg-icon.component";

export interface SoFileListItem extends Record<string, unknown> {
  id?: string | number;
  name?: string;
  url?: string;
}

export interface SoFileListIcon {
  name: string;
  color?: string;
  size?: number;
}

@Component({
  selector: 'so-file-list-ui',
  standalone: true,
  imports: [CommonModule, SoSvgIconUI],
  templateUrl: './so-file-list.component.html',
  styleUrl: './so-file-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SoFileListUI {
  items = input<SoFileListItem[]>([]);
  nameKey = input<string>('name');
  idKey = input<string>('id');
  urlKey = input<string>('url');
  showIcons = input<boolean>(true);
  showDeleteButton = input<boolean>(true);
  showOpenButton = input<boolean>(true);
  deleteIconName = input<string>('delete_forever');
  openIconName = input<string>('visibility');
  iconList = input<SoFileListIcon[] | null>(null);

  deleteItem = output<{ item: SoFileListItem; index: number }>();
  openItem = output<{ item: SoFileListItem; index: number }>();

  protected hasItems = computed(() => this.items().length > 0);

  onDelete(item: SoFileListItem, index: number): void {
    this.deleteItem.emit({ item, index });
  }

  onOpen(item: SoFileListItem, index: number): void {
    this.openItem.emit({ item, index });
  }

  getItemName(item: SoFileListItem): string {
    const key = this.nameKey();
    const value = item?.[key];
    return typeof value === 'string' ? value : '';
  }

  getItemId(item: SoFileListItem): string | number | undefined {
    const key = this.idKey();
    return item?.[key] as string | number | undefined;
  }

  getItemUrl(item: SoFileListItem): string {
    const key = this.urlKey();
    const value = item?.[key];
    return typeof value === 'string' ? value : '';
  }

  // File icon helpers (same mapping as so-filedrop)
  getIconForFile(fileName: string): string {
    if (!fileName) return 'insert_drive_file';

    const extension = fileName.split('.').pop()?.toLowerCase();

    switch (extension) {
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'webp':
      case 'svg':
        return 'image';
      case 'mp4':
      case 'avi':
      case 'mov':
      case 'webm':
        return 'videocam';
      case 'doc':
      case 'docx':
        return 'description';
      case 'xls':
      case 'xlsx':
      case 'csv':
        return 'table_chart';
      case 'pdf':
        return 'picture_as_pdf';
      case 'txt':
        return 'article';
      default:
        return 'insert_drive_file';
    }
  }

  getColorForFile(fileName: string): string {
    if (!fileName) return 'var(--color-icon, #6b7280)';

    const extension = fileName.split('.').pop()?.toLowerCase();

    switch (extension) {
      case 'pdf':
        return '--so-danger';
      case 'xls':
      case 'xlsx':
      case 'csv':
        return '--so-success';
      case 'doc':
      case 'docx':
        return '--so-info';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'webp':
      case 'svg':
        return '--so-secondary';
      default:
        return '--color-icon';
    }
  }

  trackByItem(index: number, item: SoFileListItem): string {
    const id = this.getItemId(item);
    if (id !== undefined && id !== null) return String(id);
    const name = this.getItemName(item);
    return `${name}-${index}`;
  }
}
